const VERTEX_SOURCE = `
  attribute vec2 a_position;
  attribute vec2 a_uv;
  varying vec2 v_uv;

  void main() {
    v_uv = a_uv;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const FRAGMENT_SOURCE = `
  precision mediump float;

  uniform sampler2D u_packed;
  uniform float u_half_texel;
  uniform float u_alpha_floor;
  varying vec2 v_uv;

  void main() {
    float color_u = mix(u_half_texel, 0.5 - u_half_texel, v_uv.x);
    float alpha_u = mix(0.5 + u_half_texel, 1.0 - u_half_texel, v_uv.x);
    vec3 color = texture2D(u_packed, vec2(color_u, v_uv.y)).rgb;
    float encoded_alpha = texture2D(u_packed, vec2(alpha_u, v_uv.y)).r;
    float alpha = clamp((encoded_alpha - u_alpha_floor) / (1.0 - u_alpha_floor), 0.0, 1.0);
    gl_FragColor = vec4(color, alpha);
  }
`;

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Unable to allocate a WebGL shader.");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || "Unknown WebGL shader error.";
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function createProgram(gl) {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SOURCE);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SOURCE);
  const program = gl.createProgram();
  if (!program) throw new Error("Unable to allocate a WebGL program.");
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || "Unknown WebGL link error.";
    gl.deleteProgram(program);
    throw new Error(message);
  }
  return program;
}

function sampleCornerAlpha(gl, width, height) {
  const pixel = new Uint8Array(4);
  const points = [
    [0, 0],
    [Math.max(0, width - 1), 0],
    [0, Math.max(0, height - 1)],
    [Math.max(0, width - 1), Math.max(0, height - 1)],
  ];
  let maximum = 0;
  for (const [x, y] of points) {
    gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
    maximum = Math.max(maximum, pixel[3]);
  }
  return maximum;
}

/**
 * Decode a conventional side-by-side RGB + grayscale-alpha video into one
 * transparent canvas. Ordinary VP9/H.264 decoding is used; transparency is
 * reconstructed by one WebGL draw call, avoiding browser alpha-codec gaps.
 */
export function createPackedAlphaCompositor(canvas, { alphaFloor = 2 / 255 } = {}) {
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new TypeError("A canvas is required for packed-alpha compositing.");
  }

  const gl = canvas.getContext("webgl", {
    alpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: true,
    preserveDrawingBuffer: true,
    powerPreference: "low-power",
  });
  if (!gl) throw new Error("WebGL is unavailable; using the transparent static fallback.");

  const program = createProgram(gl);
  const buffer = gl.createBuffer();
  const texture = gl.createTexture();
  if (!buffer || !texture) throw new Error("Unable to allocate packed-alpha WebGL resources.");

  const position = gl.getAttribLocation(program, "a_position");
  const uv = gl.getAttribLocation(program, "a_uv");
  const packed = gl.getUniformLocation(program, "u_packed");
  const halfTexel = gl.getUniformLocation(program, "u_half_texel");
  const alphaFloorUniform = gl.getUniformLocation(program, "u_alpha_floor");
  const vertices = new Float32Array([
    -1, -1, 0, 0,
     1, -1, 1, 0,
    -1,  1, 0, 1,
     1,  1, 1, 1,
  ]);

  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(uv);
  gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, 16, 8);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.uniform1i(packed, 0);
  gl.uniform1f(alphaFloorUniform, Math.max(0, Math.min(0.08, Number(alphaFloor) || 0)));
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.disable(gl.BLEND);
  gl.disable(gl.DEPTH_TEST);
  gl.clearColor(0, 0, 0, 0);

  let destroyed = false;
  let frameCount = 0;
  let cornerAlpha = null;
  let outputWidth = 0;
  let outputHeight = 0;

  function draw(video, { finish = false, inspectCorners = false } = {}) {
    if (destroyed) throw new Error("Packed-alpha compositor has been destroyed.");
    const packedWidth = Number(video?.videoWidth || 0);
    const height = Number(video?.videoHeight || 0);
    if (!packedWidth || !height || packedWidth % 2 !== 0) {
      throw new Error(`Invalid packed-alpha frame dimensions: ${packedWidth}x${height}.`);
    }

    const width = packedWidth / 2;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    outputWidth = width;
    outputHeight = height;
    gl.viewport(0, 0, width, height);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
    gl.uniform1f(halfTexel, 0.5 / packedWidth);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    if (finish) gl.finish();
    frameCount += 1;
    if (inspectCorners || cornerAlpha === null) {
      cornerAlpha = sampleCornerAlpha(gl, width, height);
    }
    canvas.dataset.vfxBackend = "webgl-packed-alpha";
    canvas.dataset.vfxFrameCount = String(frameCount);
    canvas.dataset.vfxCornerAlpha = String(cornerAlpha);
    return getState();
  }

  function clear() {
    if (destroyed) return;
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  function getState() {
    return Object.freeze({
      backend: "webgl-packed-alpha",
      alphaMode: "packed-side-by-side",
      frameCount,
      cornerAlpha,
      outputWidth,
      outputHeight,
    });
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    gl.deleteTexture(texture);
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    canvas.removeAttribute("data-vfx-backend");
    canvas.removeAttribute("data-vfx-frame-count");
    canvas.removeAttribute("data-vfx-corner-alpha");
  }

  return { draw, clear, getState, destroy };
}

export default createPackedAlphaCompositor;
